/**
 * EditPreviewRenderer Component
 * Feature: 006-critique-editing (crop)
 * Feature: 011-light-editing (light, combined)
 *
 * Renders a photo with edit styles applied using the editRenderer library for crop,
 * or PhotoEditorCanvas for light/combined edits.
 * This component makes it trivial to display edited versions of photos
 * anywhere in the app (critique feed, detail page, lightbox, etc.).
 */

import React from 'react'
import { applyCrop, generateEditStyles } from '../../../lib/editRenderer'
import { PhotoEditorCanvasSizable } from '../../PhotoEditor/PhotoEditorCanvasSizable'
import { DebugModeOnly } from '../../DebugModeOnly'
import type { CritiqueEdit, CritiqueEditOrPartial, CropData, LightEditData, CombinedEditData } from '../../../types/edit.types'
import { isCombinedEdit, isLightEdit } from '../../../types/edit.types'
import type { Photo } from '../../../types/photo'
import { exportImage, downloadBlob } from '../../PhotoEditor/utils/exportImage'
import logger from '../../../utils/logger'

export interface EditPreviewRendererProps {
  /** id of the photo to render */
  id: string
  /** Photo URL to render */
  photoUrl: string
  /** Photo title for alt text */
  photoTitle?: string
  /** Photo object (required for download functionality) */
  photo?: Photo
  /** Single crop data (for simple use case) */
  cropData?: CropData
  /** Single light data (for simple use case) */
  lightData?: LightEditData
  /** Edit object (for rendering with type detection) - can be full CritiqueEdit or partial with just editType/editData */
  edit?: CritiqueEditOrPartial
  /** Edit chain (for advanced use case with chaining) */
  editChain?: CritiqueEdit[]
  /** Additional CSS class names */
  className?: string
  /** Additional inline styles (merged with edit styles) */
  style?: React.CSSProperties
  /** Image loading strategy */
  loading?: 'eager' | 'lazy'
  /** Show download button for light/combined edits */
  showDownloadButton?: boolean
  /** Enable debug overlay showing renderer and dimensions */
  showDebug?: boolean
}

/**
 * Renders a photo with edit styles applied.
 *
 * Use either `cropData` (simple) or `editChain` (advanced with chaining).
 * If both are provided, `editChain` takes precedence.
 *
 * @example
 * // Simple usage with single crop
 * <EditPreviewRenderer
 *   photoUrl={photo.fullUrl}
 *   photoTitle={photo.title}
 *   cropData={{ x: 10, y: 10, width: 80, height: 80, aspectRatio: 'free' }}
 * />
 *
 * @example
 * // Advanced usage with edit chain
 * <EditPreviewRenderer
 *   photoUrl={photo.fullUrl}
 *   photoTitle={photo.title}
 *   editChain={editChainFromAPI}
 *   className="max-w-full"
 * />
 */
export function EditPreviewRenderer({
  id,
  photoUrl,
  photoTitle = 'Photo preview',
  photo,
  cropData,
  lightData,
  edit,
  editChain,
  className = '',
  style = {},
  loading = 'lazy',
  showDownloadButton = true,
  showDebug = false,
}: EditPreviewRendererProps): JSX.Element {
  // T079: Track download loading state
  const [isExporting, setIsExporting] = React.useState(false)
  // Track image dimensions for debug UI
  const [imgDimensions, setImgDimensions] = React.useState<{ width: number; height: number } | null>(null)

  // Extract crop and light data from edit object if provided
  const finalCropData = React.useMemo(() => {
    if (cropData) return cropData
    if (!edit) return undefined

    if (edit.editType === 'crop') {
      return edit.editData as CropData
    } else if (isCombinedEdit(edit)) {
      return (edit.editData as CombinedEditData).crop
    }
    return undefined
  }, [cropData, edit])

  const finalLightData = React.useMemo(() => {
    if (lightData) return lightData
    if (!edit) return undefined

    if (isLightEdit(edit)) {
      return edit.editData as LightEditData
    } else if (isCombinedEdit(edit)) {
      return (edit.editData as CombinedEditData).light
    }
    return undefined
  }, [lightData, edit])

  // T078: Download handler - wire up to exportImage utility
  // T080: Generate filename from photo title and edit ID
  const handleDownload = React.useCallback(async () => {
    if (!photo || !finalLightData) {
      logger.error('EditPreview', 'Cannot download: photo or light data missing')
      return
    }

    setIsExporting(true)
    try {
      // Generate filename: sanitize photo title + edit ID suffix
      const sanitizedTitle = photo.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 50) || 'photo'

      const editId = edit && 'id' in edit ? edit.id : null
      const editSuffix = editId ? `-edit-${editId.slice(0, 8)}` : '-edited'
      const filename = `${sanitizedTitle}${editSuffix}.jpg`

      // Export with full resolution
      const result = await exportImage(photo, finalLightData, finalCropData)

      // Trigger download
      downloadBlob(result.blob, filename)

      logger.info('EditPreview', 'Export successful:', {
        filename,
        size: `${result.width}x${result.height}`,
        renderTime: `${result.renderTime.toFixed(0)}ms`
      })
    } catch (error) {
      logger.error('EditPreview', 'Export failed:', error)
      alert('Failed to export image. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }, [photo, finalLightData, finalCropData, edit])

  // If light adjustments OR crop are present, use PhotoEditorCanvas
  if (finalLightData || finalCropData) {
    // Explicit size for PhotoEditorCanvasSizable - use width/height not maxWidth/maxHeight
    // to give the container an intrinsic size (prevents shrinking loop)
    const canvasStyle = style;

    return (
        <div className={className + " h-full w-full"}>
          <PhotoEditorCanvasSizable
            id={id}
            imageUrl={photoUrl}
            exposure={finalLightData?.exposure ?? 0}
            contrast={finalLightData?.contrast ?? 0}
            highlights={finalLightData?.highlights ?? 0}
            shadows={finalLightData?.shadows ?? 0}
            whites={finalLightData?.whites ?? 0}
            blacks={finalLightData?.blacks ?? 0}
            cropData={finalCropData}
            originalImageWidth={photo?.width ?? undefined}
            originalImageHeight={photo?.height ?? undefined}
            showDebug={showDebug}
            style={canvasStyle}
          />
          {/* T077: Download button */}
          {showDownloadButton && photo && finalLightData && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleDownload}
                disabled={isExporting}
                className={`
                  px-4 py-2 rounded font-medium
                  ${isExporting
                    ? 'bg-muted text-muted cursor-not-allowed'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                  }
                  transition-colors duration-200
                `}
                title="Download full-resolution image with adjustments applied"
              >
              {/* T079: Loading indicator */}
              {isExporting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Exporting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Suggested Edit
                </span>
              )}
            </button>
          </div>
        )}
        </div>
    )
  }

  // Otherwise, use CSS-based crop rendering (original behavior)
  const editStyles = React.useMemo(() => {
    if (editChain && editChain.length > 0) {
      // Use edit chain (advanced use case)
      return generateEditStyles(editChain)
    } else if (finalCropData) {
      // Use single crop data (simple use case)
      return applyCrop(finalCropData)
    }
    // No edits to apply
    return {}
  }, [editChain, finalCropData])

  // Merge edit styles with custom styles
  const mergedStyles = {
    ...editStyles,
    ...style,
  }

  return (
    <div className="relative inline-block">
      <img
        src={photoUrl}
        alt={photoTitle}
        className={className}
        style={mergedStyles}
        loading={loading}
        onLoad={(e) => {
          const img = e.currentTarget
          setImgDimensions({ width: img.naturalWidth, height: img.naturalHeight })
        }}
      />
      {/* Debug Overlay */}
      <DebugModeOnly>
        {imgDimensions && (
          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded font-mono z-10">
            <div className="flex items-center gap-1">
              <span>IMG</span>
              <span className="text-gray-300">
                {imgDimensions.width}x{imgDimensions.height}
              </span>
            </div>
          </div>
        )}
      </DebugModeOnly>
    </div>
  )
}

/**
 * Side-by-side comparison of original and edited versions
 *
 * @example
 * <EditPreviewComparison
 *   photoUrl={photo.fullUrl}
 *   photoTitle={photo.title}
 *   photo={photo}
 *   editChain={edits}
 * />
 */
export function EditPreviewComparison({
  id,
  photoUrl,
  photoTitle = 'Photo comparison',
  photo,
  cropData,
  lightData,
  edit,
  editChain,
}: Pick<EditPreviewRendererProps, 'id' | 'photoUrl' | 'photoTitle' | 'photo' | 'cropData' | 'lightData' | 'edit' | 'editChain'>): JSX.Element {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [originalImgDimensions, setOriginalImgDimensions] = React.useState<{ width: number; height: number } | null>(null)

  // Extract crop data from edit object if provided
  const finalCropData = React.useMemo(() => {
    if (cropData) return cropData
    if (!edit) return undefined

    if (edit.editType === 'crop') {
      return edit.editData as CropData
    } else if ('editData' in edit && typeof edit.editData === 'object' && edit.editData !== null && 'crop' in edit.editData) {
      return (edit.editData as any).crop as CropData
    }
    return undefined
  }, [cropData, edit])

  // Extract light data from edit object if provided
  const finalLightData = React.useMemo(() => {
    if (lightData) return lightData
    if (!edit) return undefined

    if (edit.editType === 'light') {
      return edit.editData as LightEditData
    } else if ('editData' in edit && typeof edit.editData === 'object' && edit.editData !== null && 'light' in edit.editData) {
      return (edit.editData as any).light as LightEditData
    }
    return undefined
  }, [lightData, edit])

  return (
    <div className="grid grid-cols-2 gap-4 h-full">
      <div className="flex flex-col min-h-0">
        <h3 className="text-sm font-medium text-muted mb-2 flex-shrink-0">Original</h3>
        <div className="flex-1 flex items-center justify-center min-h-0 min-w-0 relative">
          <img
            src={photoUrl}
            alt={`${photoTitle} - Original`}
            className="w-full h-full object-contain rounded"
            loading="lazy"
            onLoad={(e) => {
              const img = e.currentTarget
              setOriginalImgDimensions({ width: img.naturalWidth, height: img.naturalHeight })
            }}
          />
          {/* Debug Overlay for Original */}
          <DebugModeOnly>
            {originalImgDimensions && (
              <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded font-mono z-10">
                <div className="flex items-center gap-1">
                  <span>IMG</span>
                  <span className="text-gray-300">
                    {originalImgDimensions.width}x{originalImgDimensions.height}
                  </span>
                </div>
              </div>
            )}
          </DebugModeOnly>
        </div>
      </div>
      <div className="flex flex-col min-h-0">
        <h3 className="text-sm font-medium text-muted mb-2 flex-shrink-0">Suggested Edit</h3>
        <div ref={containerRef} className="flex-1 flex items-center justify-center min-h-0 min-w-0 overflow-hidden">
          <EditPreviewRenderer
              id={id}
              photoUrl={photoUrl}
              photoTitle={`${photoTitle} - Edited`}
              photo={photo}
              cropData={finalCropData}
              lightData={finalLightData}
              edit={edit}
              editChain={editChain}
              className="rounded"
            />
        </div>
      </div>
    </div>
  )
}

export default EditPreviewRenderer
